import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Billing.css';
import { useTheme } from '../contexts/ThemeContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import Footer from './Footer';
import { apiRequest } from '../services/apiInterceptor';
import { getReferralCode, clearReferralCode } from '../utils/referralCodeStorage';

// Component for individual cancel subscription card
function CancelSubscriptionCard({ subscription, package: pkg, displayStatus, statusClass, canCancel, cancellingSubscriptionId, onCancelSubscription }) {
  const [isFree, setIsFree] = useState(false);
  const [isLoadingRate, setIsLoadingRate] = useState(true);
  
  useEffect(() => {
    const checkIfFree = async () => {
      try {
        if (subscription.planRateId) {
          const rateDetails = await apiRequest(`/api/plan/rate/${subscription.planRateId}`);
          const monthlyPrice = rateDetails.monthlyPrice || 0;
          const annualPrice = rateDetails.annualPrice || 0;
          const isFreeRate = monthlyPrice === 0 && annualPrice === 0;
          setIsFree(isFreeRate);
        } else {
          // Fallback: check by package name if no rate ID
          const packageName = pkg?.name || pkg?.Name || '';
          const isFreePackage = packageName.toLowerCase().includes('free');
          setIsFree(isFreePackage);
        }
      } catch (error) {
        console.warn('Error checking if subscription is free:', error);
        // Fallback: check by package name
        const packageName = pkg?.name || pkg?.Name || '';
        const isFreePackage = packageName.toLowerCase().includes('free');
        setIsFree(isFreePackage);
      } finally {
        setIsLoadingRate(false);
      }
    };
    
    checkIfFree();
  }, [subscription.planRateId, pkg?.name, pkg?.Name]);
  
  return (
    <div className="cancel-subscription-card">
      <div className="cancel-card-header">
        <h3>{pkg?.name || pkg?.Name || 'Unknown Package'}</h3>
      </div>
      
      <div className="cancel-card-details">
        <p className="subscription-status">
          Status: <span className={`status-${statusClass}`}>
            {displayStatus}
          </span>
        </p>
        
        {/* Show loading state while checking if free */}
        {isLoadingRate ? (
          <p>Loading plan details...</p>
        ) : (
          <>
            {/* Only show billing information for non-free plans */}
            {!isFree && (
              <>
                <p><strong>Billing:</strong> {subscription.paymentCycle || 'Monthly'}</p>
                <p className="next-billing">
                  <strong>Next billing:</strong> {
                    subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : 
                    subscription.nextBillingDate ? new Date(subscription.nextBillingDate).toLocaleDateString() : 'N/A'
                  }
                </p>
              </>
            )}
            
            {/* Show free plan indicator */}
            {isFree && (
              <p className="free-plan-indicator">
                <strong>Plan Type:</strong> <span className="free-plan-text">Free Plan</span>
              </p>
            )}
          </>
        )}
      </div>

      <div className="cancel-card-actions">
        <button 
          className="cancel-subscription-button"
          onClick={() => onCancelSubscription(subscription.id || subscription.Id)}
          disabled={!canCancel || cancellingSubscriptionId === (subscription.id || subscription.Id)}
        >
          {cancellingSubscriptionId === (subscription.id || subscription.Id) 
            ? 'Processing...' 
            : canCancel 
              ? 'Cancel This Subscription' 
              : subscription.status?.toLowerCase() === 'canceled' 
                ? 'Already Canceled' 
                : 'Cannot Cancel'
          }
        </button>
      </div>
    </div>
  );
}

function Billing() {
  const navigate = useNavigate();
  
  // State for the three-step workflow
  const [currentStep, setCurrentStep] = useState(1); // 1: packages, 2: plans, 3: payment
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedRate, setSelectedRate] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(false);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // State for current subscription package info
  const [currentSubscriptionPackage, setCurrentSubscriptionPackage] = useState(null);
  const [allUserSubscriptions, setAllUserSubscriptions] = useState([]);
  const [subscribedPackages, setSubscribedPackages] = useState([]);
  
  // State for plan types
  const [planTypes, setPlanTypes] = useState({});
  
  // State for current subscription plan and rate (for upgrade/downgrade logic)
  const [currentSubscriptionPlan, setCurrentSubscriptionPlan] = useState(null);
  const [currentSubscriptionRate, setCurrentSubscriptionRate] = useState(null);
  
  // State for cancel subscription flow
  const [showCancelFlow, setShowCancelFlow] = useState(false);
  const [cancellingSubscriptionId, setCancellingSubscriptionId] = useState(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  
  // State for promotional code functionality
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [discountedPrice, setDiscountedPrice] = useState(null);
  
  // State for dual referral code sources
  const [backendReferralCode, setBackendReferralCode] = useState(null);
  const [localStorageReferralCode, setLocalStorageReferralCode] = useState(null);
  const [selectedReferralSource, setSelectedReferralSource] = useState(null); // 'backend' | 'localStorage' | null
  const [showReferralSelection, setShowReferralSelection] = useState(false);
  
  const { isDarkMode } = useTheme();
  const { hasActiveSubscription, subscription, refreshSubscription } = useSubscription();

  // Mobile detection and utilities
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkIsMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);



  // Scroll to top utility for mobile navigation
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Enhanced package selection for mobile
  const handleMobilePackageSelection = (packageObj) => {
    if (isMobile) {
      // Add haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      // Scroll to top when navigating to next step on mobile
      setTimeout(scrollToTop, 100);
    }
    handlePackageSelection(packageObj);
  };

  // Enhanced plan selection for mobile
  const handleMobilePlanSelection = (plan) => {
    if (isMobile) {
      // Add haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      // Scroll to top when navigating to next step on mobile
      setTimeout(scrollToTop, 100);
    }
    handlePlanSelection(plan);
  };

  // Enhanced back navigation with scroll to top
  const handleMobileBackToPackages = () => {
    if (isMobile) {
      scrollToTop();
    }
    handleBackToPackages();
  };

  const handleMobileBackToPlans = () => {
    if (isMobile) {
      scrollToTop();
    }
    handleBackToPlans();
  };

  // Computed value to check if user has any active subscriptions
  const hasAnyActiveSubscription = hasActiveSubscription || subscribedPackages.some(subPkg => 
    subPkg?.subscription?.status && subPkg?.subscription?.status?.toLowerCase() === 'active'
  );

  // Check for returning users from payment
  useEffect(() => {
    const checkPendingSubscription = async () => {
      const pendingSubscription = sessionStorage.getItem('pendingSubscription');
      if (pendingSubscription === 'true') {
        // console.log('User returned from payment, refreshing subscription status...');
        sessionStorage.removeItem('pendingSubscription');
        
        // Wait a moment for payment processing, then refresh
        setTimeout(async () => {
          await refreshSubscription();
        }, 2000);
      }
    };

    checkPendingSubscription();
  }, [refreshSubscription]);

  // Fetch current subscription package information and all user subscriptions
  const fetchAllUserSubscriptionsAndPackages = async () => {
    setSubscriptionsLoading(true);
    try {
      // console.log('Fetching user subscriptions...');
      
      // Get all user subscriptions - fetch this regardless of hasActiveSubscription
      const userSubscriptions = await apiRequest('/api/usersubscription');
      // console.log('User subscriptions:', userSubscriptions);
      setAllUserSubscriptions(userSubscriptions || []);
      
      if (userSubscriptions && userSubscriptions.length > 0) {
          // Process all subscriptions to get their package information
          const subscriptionPackages = await Promise.all(
            userSubscriptions.map(async (sub) => {
              try {
                if (sub.planRateId) {
                  // Fetch the plan rate details to get package ID
                  const rateDetails = await apiRequest(`/api/plan/rate/${sub.planRateId}`);
                  const packageId = rateDetails.packageId || rateDetails.PackageId;
                  
                  if (packageId) {
                    // Fetch the package information
                    const packageInfo = await apiRequest(`/api/package/${packageId}`);
                    return {
                      subscription: sub,
                      package: packageInfo,
                      packageId: packageId
                    };
                  }
                }
                return null;
              } catch (error) {
                console.warn('Error fetching package for subscription:', sub, error);
                return null;
              }
            })
          );
          
          // Filter out null results
          const validSubscriptionPackages = subscriptionPackages.filter(item => item !== null);
          // console.log('Valid subscription packages:', validSubscriptionPackages);
          // console.log('Each subscription package:', validSubscriptionPackages.map((item, idx) => ({
          //   index: idx,
          //   subscription: item?.subscription,
          //   package: item?.package,
          //   hasSubscription: !!item?.subscription,
          //   hasPackage: !!item?.package
          // })));
          setSubscribedPackages(validSubscriptionPackages);
          
          // Fetch plan types for all subscriptions
          const planTypePromises = userSubscriptions.map(async (sub) => {
            const planType = await getPlanType(sub);
            return { subscriptionId: sub.id || sub.Id, planType };
          });
          
          const planTypeResults = await Promise.all(planTypePromises);
          const planTypesMap = {};
          planTypeResults.forEach(result => {
            planTypesMap[result.subscriptionId] = result.planType;
          });
          setPlanTypes(planTypesMap);
          
          // Find the current subscription package (prioritize current=true, then most recent active)
          let currentSub = userSubscriptions.find(sub => sub.current === true);
          
          // If no subscription is marked as current, find the most recent active subscription
          if (!currentSub) {
            const activeSubscriptions = userSubscriptions.filter(sub => 
              sub.status && sub.status.toLowerCase() === 'active'
            );
            if (activeSubscriptions.length > 0) {
              // Sort by start date (most recent first) and take the first one
              currentSub = activeSubscriptions.sort((a, b) => 
                new Date(b.startDate) - new Date(a.startDate)
              )[0];
            }
          }
          
          if (currentSub) {
            const currentSubPackage = validSubscriptionPackages.find(
              item => item?.subscription?.id === currentSub.id || item?.subscription?.Id === currentSub.Id
            );
            setCurrentSubscriptionPackage(currentSubPackage?.package || null);
          }
          
          // console.log('All subscription packages:', validSubscriptionPackages);
        } else {
          setSubscribedPackages([]);
          setCurrentSubscriptionPackage(null);
        }
      } catch (error) {
        console.error('Error fetching subscription packages:', error);
        setAllUserSubscriptions([]);
        setSubscribedPackages([]);
        setCurrentSubscriptionPackage(null);
      } finally {
        setSubscriptionsLoading(false);
      }
  };

  useEffect(() => {
    fetchAllUserSubscriptionsAndPackages();
  }, []); // Remove dependency on hasActiveSubscription since we always want to fetch

  // Step 1: Fetch packages when component mounts
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const packagesResponse = await apiRequest('/api/package');
        setPackages(packagesResponse || []);
        
        // console.log('Loaded packages:', packagesResponse);
        
        // If no packages from API, add some fallback packages for testing
        if (!packagesResponse || packagesResponse.length === 0) {
          // console.log('No packages from API, using fallback packages');
          const fallbackPackages = [
            { 
              id: 1, 
              name: 'CBSE + STEP 1', 
              description: 'Comprehensive package for CBSE and STEP 1 exam preparation'
            },
            { 
              id: 2, 
              name: 'STEP 2 CK', 
              description: 'Complete preparation package for STEP 2 Clinical Knowledge exam'
            }
          ];
          setPackages(fallbackPackages);
        }
      } catch (err) {
        console.error('Error fetching packages:', err);
        setError('Failed to load packages. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  // Auto-select first package when packages load (for streamlined experience)
  // Wait for both packages and subscriptions to load before auto-selecting
  useEffect(() => {
    if (packages.length > 0 && !selectedPackage && !loading && !subscriptionsLoading && currentStep === 1) {
      handlePackageSelection(packages[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packages, loading, subscriptionsLoading]);

  // Step 2: Fetch plans and filter by selected package
  const handlePackageSelection = async (packageObj) => {
    try {
      setPlansLoading(true);
      setSelectedPackage(packageObj);
      // Keep on step 1 since we now have combined view (step 1 = plans shown inline)
      // setCurrentStep(2); // Removed - no longer needed with combined flow
      setError(null);
      
      // Check if this is a subscribed package and get current plan info
      const packageSubscription = subscribedPackages.find(subPkg => 
        (packageObj.id || packageObj.Id) === (subPkg.package?.id || subPkg.package?.Id)
      );
      
      if (packageSubscription) {
        // Get current subscription plan and rate details
        try {
          const subscription = packageSubscription.subscription;
          if (subscription && subscription.planRateId) {
            const rateDetails = await apiRequest(`/api/plan/rate/${subscription.planRateId}`);
            const planDetails = await apiRequest(`/api/plan/${rateDetails.planId || rateDetails.PlanId}`);
            
            setCurrentSubscriptionPlan(planDetails);
            setCurrentSubscriptionRate(rateDetails);
            // console.log('Current subscription plan:', planDetails);
            // console.log('Current subscription rate:', rateDetails);
            // console.log('Selected package is subscribed, enabling upgrade/downgrade logic');
          }
        } catch (error) {
          console.warn('Error fetching current subscription plan details:', error);
        }
      } else {
        // Clear current subscription plan info if not a subscribed package
        setCurrentSubscriptionPlan(null);
        setCurrentSubscriptionRate(null);
        // console.log('Selected package is not subscribed, normal plan selection');
      }
      
      // Fetch all plans
      const allPlans = await apiRequest('/api/plan');
      setPlans(allPlans || []);
      
      // Fetch rates and info for each plan and filter by selected package
      const plansWithDetails = await Promise.all(
        (allPlans || []).map(async (plan) => {
          try {
            const planId = plan.id || plan.Id;
            
            // Fetch rates and info in parallel
            const [rates, info] = await Promise.all([
              apiRequest(`/api/plan/${planId}/rate`).catch(() => []),
              apiRequest(`/api/plan/${planId}/info`).catch(() => [])
            ]);
            
            // Filter rates that match the selected package
            const packageRates = (rates || []).filter(rate => 
              (rate.packageId || rate.PackageId) === (packageObj.id || packageObj.Id)
            );
            
            return {
              ...plan,
              rates: packageRates,
              info: info || []
            };
          } catch (err) {
            console.warn(`Failed to fetch details for plan ${plan.id || plan.Id}:`, err);
            return {
              ...plan,
              rates: [],
              info: []
            };
          }
        })
      );
      
      // Only show plans that have rates for this package
      const filteredPlansWithDetails = plansWithDetails.filter(plan => 
        plan.rates && plan.rates.length > 0
      );
      
      // If no plans have rates for this package, create some fallback plans for testing
      if (filteredPlansWithDetails.length === 0 && plansWithDetails.length > 0) {
        // console.log('No plans have rates for this package, creating fallback rates');
        const plansWithFallbackRates = plansWithDetails.slice(0, 4).map((plan, index) => {
          const planNames = ['free', 'Basic', 'Standard', 'Premium'];
          const prices = [
            { monthly: 0, annual: 0 },
            { monthly: 20, annual: 200 },
            { monthly: 40, annual: 400 },
            { monthly: 100, annual: 1000 }
          ];
          
          return {
            ...plan,
            name: plan.name || plan.Name || planNames[index],
            rates: [{
              packageId: packageObj.id || packageObj.Id,
              monthlyPrice: prices[index]?.monthly,
              annualPrice: prices[index]?.annual,
              monthlyPricePaymentId: `monthly_${plan.id || plan.Id}_${packageObj.id || packageObj.Id}`,
              annualPricePaymentId: `annual_${plan.id || plan.Id}_${packageObj.id || packageObj.Id}`
            }]
          };
        });
        setFilteredPlans(plansWithFallbackRates);
      } else {
        setFilteredPlans(filteredPlansWithDetails);
      }
      
      // console.log('Filtered plans for package:', packageObj);
      // console.log('Total plans fetched:', plansWithDetails.length);
      // console.log('Plans with rates for this package:', filteredPlansWithDetails.length);
      
      // Debug each plan's details
      (filteredPlansWithDetails.length > 0 ? filteredPlansWithDetails : plansWithDetails).forEach(plan => {
        // console.log(`Plan ${plan.name || plan.Name}:`, {
        //   id: plan.id || plan.Id,
        //   rates: plan.rates,
        //   ratesCount: plan.rates?.length || 0,
        //   info: plan.info,
        //   infoCount: plan.info?.length || 0
        // });
      });
      
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load plans. Please try again.');
    } finally {
      setPlansLoading(false);
    }
  };

  // Handle plan selection
  const handlePlanSelection = async (plan) => {
    setSelectedPlan(plan);
    
    // Auto-select the rate based on billing period
    const rate = plan.rates.find(r => {
      if (billingPeriod === 'monthly') {
        return r.monthlyPrice !== null && r.monthlyPrice !== undefined;
      } else {
        return r.annualPrice !== null && r.annualPrice !== undefined;
      }
    });
    
    setSelectedRate(rate);
    
    // Check if this is a free plan (price is 0)
    const price = billingPeriod === 'monthly' ? rate?.monthlyPrice : rate?.annualPrice;
    const planName = plan.name || plan.Name || 'Plan';
    const isFree = price === 0 || planName.toLowerCase() === 'free';
    
    if (isFree && rate) {
      // For free plans, automatically subscribe without going to payment page
      try {
        setError(null);
        
        // Prepare the request body
        const requestBody = {
          rateId: rate.id || rate.Id,
          paymentCycle: billingPeriod === 'monthly' ? 'Monthly' : 'Annual'
        };

        // console.log('Subscribing to free plan with data:', requestBody);

        // Send subscription request to backend
        const response = await apiRequest('/api/usersubscription/subscribe', {
          method: 'POST',
          body: JSON.stringify(requestBody)
        });

        // console.log('Free subscription response:', response);

        // For free plans, there should be no checkout URL - subscription should be created directly
        if (response) {
          // console.log('Free subscription created successfully');
          
          // Refresh subscription data
          await refreshSubscription();
          await fetchAllUserSubscriptionsAndPackages();
          
          // Navigate to dashboard after successful free plan activation
          navigate('/dashboard');
        } else {
          setError('Invalid response from server. Please try again.');
        }
      } catch (error) {
        console.error('Free subscription error:', error);
        setError(error.message || 'Failed to activate free plan. Please try again.');
      }
    } else {
      // For paid plans, proceed to payment confirmation page
      setCurrentStep(3);
    }
  };

  // Handle billing period change
  const handleBillingPeriodChange = (period) => {
    setBillingPeriod(period);
    
    // Update selected rate if a plan is already selected
    if (selectedPlan) {
      const rate = selectedPlan.rates.find(r => {
        if (period === 'monthly') {
          return r.monthlyPrice !== null && r.monthlyPrice !== undefined;
        } else {
          return r.annualPrice !== null && r.annualPrice !== undefined;
        }
      });
      setSelectedRate(rate);
    }
  };

  // Handle Buy Now action
  const handleBuyNow = async (e) => {
    e.preventDefault();
    
    if (!selectedRate) {
      setError('Please select a plan and billing option.');
      return;
    }

    try {
      setError(null);
      
      // Prepare the request body
      const requestBody = {
        rateId: selectedRate.id || selectedRate.Id,
        paymentCycle: billingPeriod === 'monthly' ? 'Monthly' : 'Annual'
      };

      // Add promotional code if applied
      if (appliedPromo && promoCode) {
        requestBody.promoCode = promoCode.trim();
      }

      // console.log('Subscribing with data:', requestBody);

      // Send subscription request to backend
      const response = await apiRequest('/api/usersubscription/subscribe', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      // console.log('Subscription response:', response);

      // Check if we got a checkout URL
      if (response && response.checkoutUrl) {
        // Store the pending subscription so SubscriptionContext can poll for it after Stripe redirect
        sessionStorage.setItem('pendingSubscription', 'true');
        sessionStorage.setItem('pendingSubscriptionId', String(response.id || response.Id));
        console.log('[Billing] Stored pending subscription id:', response.id || response.Id);
        
        // Clear localStorage referral code if it was used
        if (selectedReferralSource === 'localStorage' || localStorageReferralCode) {
          clearReferralCode();
          console.log('[Billing] Cleared localStorage referral code after successful subscription');
        }
        
        // Redirect to Stripe checkout
        window.location.href = response.checkoutUrl;
      } else if (response) {
        // If no checkout URL but response is valid (200 OK), 
        // subscription was created successfully - redirect to billing page
        // console.log('Subscription created successfully without checkout URL');
        
        // Clear localStorage referral code if it was used
        if (selectedReferralSource === 'localStorage' || localStorageReferralCode) {
          clearReferralCode();
          console.log('[Billing] Cleared localStorage referral code after successful subscription');
        }
        
        // Refresh subscription data
        await refreshSubscription();
        await fetchAllUserSubscriptionsAndPackages();
        
        // Reset to step 1 to show updated subscription status
        handleBackToPackages();
        
        // Show success message
        setSuccessMessage('Subscription activated successfully!');
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError('Invalid response from server. Please try again.');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setError(error.message || 'Failed to start subscription. Please try again.');
    }
  };

  // Reset to step 1
  const handleBackToPackages = () => {
    setCurrentStep(1);
    setSelectedPackage(null);
    setSelectedPlan(null);
    setSelectedRate(null);
    setFilteredPlans([]);
    // Clear current subscription plan info when going back to packages
    setCurrentSubscriptionPlan(null);
    setCurrentSubscriptionRate(null);
    // Clear promotional code data
    setPromoCode('');
    setAppliedPromo(null);
    setDiscountedPrice(null);
    setPromoError('');
  };

  // Reset to step 2
  const handleBackToPlans = () => {
    setCurrentStep(2);
    setSelectedPlan(null);
    setSelectedRate(null);
    // Clear promotional code data
    setPromoCode('');
    setAppliedPromo(null);
    setDiscountedPrice(null);
    setPromoError('');
  };

  // Handle cancel subscription flow
  const handleShowCancelFlow = () => {
    setShowCancelFlow(true);
  };

  const handleBackFromCancel = () => {
    setShowCancelFlow(false);
    setCancellingSubscriptionId(null);
    setShowCancelConfirmation(false);
  };

  const handleCancelSubscription = (subscriptionId) => {
    setCancellingSubscriptionId(subscriptionId);
    setShowCancelConfirmation(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancellingSubscriptionId) return;

    try {
      setError(null);
      
      // Send cancel request to backend
      const response = await apiRequest(`/api/usersubscription/${cancellingSubscriptionId}/cancel`, {
        method: 'POST'
      });

      // console.log('Cancel subscription response:', response);

      // Refresh subscription data
      await refreshSubscription();
      await fetchAllUserSubscriptionsAndPackages();
      
      // Reset cancel flow states
      setShowCancelConfirmation(false);
      setCancellingSubscriptionId(null);
      setShowCancelFlow(false);
      
      // Show success message in UI instead of alert
      setSuccessMessage('Subscription cancelled successfully');
      // Clear the success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
      
    } catch (error) {
      console.error('Cancel subscription error:', error);
      setError(error.message || 'Failed to cancel subscription. Please try again.');
    }
  };

  // Promotional code functions
  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError('Please enter a promotion code');
      return;
    }

    if (!selectedRate) {
      setPromoError('Please select a plan first');
      return;
    }

    console.log('[Billing] Applying promo code:', promoCode);
    setPromoLoading(true);
    setPromoError('');

    try {
      const requestData = {
        rateId: selectedRate.id || selectedRate.Id,
        paymentCycle: billingPeriod === 'monthly' ? 'Monthly' : 'Annual',
        promoCode: promoCode.trim()
      };

      console.log('[Billing] Promo code request data:', requestData);

      const promoDetails = await apiRequest('/api/usersubscription/promo-code', {
        method: 'POST',
        body: JSON.stringify(requestData)
      });

      console.log('[Billing] Promo code response:', promoDetails);

      if (promoDetails.isValid) {
        setAppliedPromo(promoDetails);
        calculateDiscountedPrice(promoDetails);
        setPromoError('');
        setSuccessMessage(`Promotion code "${promoDetails.code}" applied successfully!`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        console.warn('[Billing] Promo code is invalid');
        setPromoError('Invalid promotion code');
        setAppliedPromo(null);
        setDiscountedPrice(null);
      }
    } catch (error) {
      console.error('[Billing] Promo code validation error:', error);
      if (error.status === 404) {
        setPromoError('Invalid or expired promotion code');
      } else {
        setPromoError('Unable to validate promotion code. Please try again.');
      }
      setAppliedPromo(null);
      setDiscountedPrice(null);
    } finally {
      setPromoLoading(false);
    }
  };

  // Fetch and apply referral promo code from API and localStorage
  const fetchAndApplyReferralPromoCode = async () => {
    if (!selectedRate) {
      console.log('[Billing] Skipping referral promo code fetch - missing selectedRate');
      return;
    }

    console.log('[Billing] Fetching referral promo codes from both API and localStorage...');
    setPromoLoading(true);
    setPromoError('');

    let backendCode = null;
    let localCode = null;

    // 1. Fetch from backend API
    try {
      const promoDetails = await apiRequest('/api/usersubscription/promo-code', {
        method: 'GET'
      });

      console.log('[Billing] Backend referral promo code response:', promoDetails);

      if (promoDetails && promoDetails.code) {
        backendCode = promoDetails.code;
        setBackendReferralCode(backendCode);
        console.log('[Billing] Backend referral code found:', backendCode);
      } else {
        console.log('[Billing] No backend referral promo code found for user');
      }
    } catch (error) {
      if (error.status === 404) {
        console.log('[Billing] No backend referral promo code found (404)');
      } else {
        console.error('[Billing] Error fetching backend referral promo code:', error);
      }
    }

    // 2. Check localStorage
    try {
      const storedCode = getReferralCode();
      if (storedCode) {
        localCode = storedCode;
        setLocalStorageReferralCode(localCode);
        console.log('[Billing] localStorage referral code found:', localCode);
      } else {
        console.log('[Billing] No localStorage referral code found');
      }
    } catch (error) {
      console.error('[Billing] Error reading localStorage referral code:', error);
    }

    // 3. Determine which code to use
    const hasBothCodes = backendCode && localCode;
    const hasOnlyBackend = backendCode && !localCode;
    const hasOnlyLocal = !backendCode && localCode;

    console.log('[Billing] Referral code status:', {
      backend: backendCode,
      localStorage: localCode,
      hasBothCodes,
      hasOnlyBackend,
      hasOnlyLocal
    });

    if (hasBothCodes) {
      // Both codes exist - show selection UI
      console.log('[Billing] Both referral codes available - showing selection UI');
      setShowReferralSelection(true);
      setPromoLoading(false);
      return;
    } else if (hasOnlyBackend) {
      // Only backend code - auto-apply it
      console.log('[Billing] Auto-applying backend referral code:', backendCode);
      setSelectedReferralSource('backend');
      await applyReferralCodeBySource('backend', backendCode);
    } else if (hasOnlyLocal) {
      // Only localStorage code - auto-apply it
      console.log('[Billing] Auto-applying localStorage referral code:', localCode);
      setSelectedReferralSource('localStorage');
      await applyReferralCodeBySource('localStorage', localCode);
    } else {
      // No codes available
      console.log('[Billing] No referral codes available');
      setPromoLoading(false);
    }
  };

  // Apply referral code from specific source
  const applyReferralCodeBySource = async (source, code) => {
    if (!code || !selectedRate) {
      console.warn('[Billing] Cannot apply referral code - missing code or selectedRate');
      setPromoLoading(false);
      return;
    }

    setPromoLoading(true);
    setPromoError('');

    try {
      setPromoCode(code);
      
      // Validate and apply the promo code
      const requestData = {
        rateId: selectedRate.id || selectedRate.Id,
        paymentCycle: billingPeriod === 'monthly' ? 'Monthly' : 'Annual',
        promoCode: code.trim()
      };

      console.log(`[Billing] Validating ${source} referral promo code:`, requestData);

      const validatedPromo = await apiRequest('/api/usersubscription/promo-code', {
        method: 'POST',
        body: JSON.stringify(requestData)
      });

      if (validatedPromo.isValid) {
        setAppliedPromo(validatedPromo);
        calculateDiscountedPrice(validatedPromo);
        setPromoError('');
        setShowReferralSelection(false);
        console.log(`[Billing] ${source} referral promo code applied successfully:`, code);
      } else {
        console.warn(`[Billing] ${source} referral promo code is not valid:`, code);
        setPromoCode('');
        setAppliedPromo(null);
        setDiscountedPrice(null);
        setPromoError(`The ${source} referral code is not valid or has expired.`);
      }
    } catch (error) {
      console.error(`[Billing] Error validating ${source} referral promo code:`, error);
      setPromoCode('');
      setAppliedPromo(null);
      setDiscountedPrice(null);
      
      // Check for 404 or specific error messages
      if (error.status === 404 || error.message?.includes('Invalid or expired promo code')) {
        setPromoError('Invalid or expired code.');
      } else {
        setPromoError(`Unable to validate the ${source} referral code. Please try again.`);
      }
    } finally {
      setPromoLoading(false);
    }
  };

  const calculateDiscountedPrice = (promoDetails) => {
    if (!selectedRate || !promoDetails) {
      console.warn('[Billing] Cannot calculate discount - missing selectedRate or promoDetails');
      return;
    }

    console.log('[Billing] Calculating discounted price with:', { promoDetails, billingPeriod, selectedRate });

    const originalPrice = billingPeriod === 'monthly' 
      ? selectedRate.monthlyPrice 
      : selectedRate.annualPrice;

    console.log('[Billing] Original price:', originalPrice);

    let discountAmount = 0;
    
    // Check if it's a percentage discount by checking if discountPercentage exists and is valid
    const isPercentageDiscount = promoDetails.isPercentageDiscount || 
      (promoDetails.discountPercentage != null && promoDetails.discountPercentage > 0);
    
    if (isPercentageDiscount && promoDetails.discountPercentage != null) {
      discountAmount = (originalPrice * promoDetails.discountPercentage) / 100;
      console.log('[Billing] Percentage discount:', promoDetails.discountPercentage + '%', '=', discountAmount);
    } else if (promoDetails.discountAmount != null) {
      discountAmount = promoDetails.discountAmount;
      console.log('[Billing] Fixed discount amount:', discountAmount);
    } else {
      console.warn('[Billing] No valid discount found in promo details:', promoDetails);
    }

    const finalPrice = Math.max(0, originalPrice - discountAmount);
    const discountResult = {
      originalPrice,
      discountAmount,
      finalPrice,
      savings: discountAmount
    };
    
    console.log('[Billing] Calculated discount result:', discountResult);
    setDiscountedPrice(discountResult);
  };

  // Helper function to determine if promo is percentage-based
  const isPercentageDiscount = (promo) => {
    return promo?.isPercentageDiscount || 
      (promo?.discountPercentage != null && promo.discountPercentage > 0);
  };

  const handleRemovePromoCode = () => {
    setPromoCode('');
    setAppliedPromo(null);
    setDiscountedPrice(null);
    setPromoError('');
  };

  // Clear promo code when plan or billing period changes
  React.useEffect(() => {
    if (appliedPromo) {
      setPromoCode('');
      setAppliedPromo(null);
      setDiscountedPrice(null);
      setPromoError('');
    }
  }, [selectedRate, billingPeriod]);

  // Fetch and apply referral promo code when reaching payment step
  React.useEffect(() => {
    console.log('[Billing] Payment step effect - currentStep:', currentStep, 'selectedRate:', !!selectedRate, 'appliedPromo:', !!appliedPromo);
    
    if (currentStep === 3 && selectedRate && !appliedPromo) {
      console.log('[Billing] Fetching referral promo code from API...');
      fetchAndApplyReferralPromoCode();
    }
  }, [currentStep, selectedRate]);

  // Render loading state
  if (loading) {
    return (
      <div className={`billing-page-wrapper ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className="billing-container">
          <div className="loading-message">Loading packages...</div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && currentStep === 1) {
    return (
      <div className={`billing-page-wrapper ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className="billing-container">
          <div className="error-message">{error}</div>
          <button 
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`billing-page-wrapper ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="billing-container">
        {/* Optimized Header - Clean and focused */}
        <div className="billing-header">
          <h1 className={isDarkMode ? 'header-dark' : 'header-light'}>
            {hasAnyActiveSubscription ? 'Manage Your Subscription' : 'Start Learning Today'}
          </h1>
          
          {!hasAnyActiveSubscription && currentStep !== 3 && (
            <p className="billing-subtitle">
              Choose a plan that fits your needs. Start free, upgrade anytime.
            </p>
          )}

          {/* Trust badges - only show for new users */}
          {!hasAnyActiveSubscription && currentStep !== 3 && (
            <div className="trust-badges">
              <span className="trust-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
                No credit card required for free plan
              </span>
              <span className="trust-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
                Cancel anytime
              </span>
              <span className="trust-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
                Instant access
              </span>
            </div>
          )}
          
          {hasAnyActiveSubscription && (
            <div className="current-subscription-info">
              <div className="current-plan-display">
                <h2 className="current-plan-title">
                  {/* Always show the plan marked as 'Current Active Plan' */}
                  {(() => {
                    // Find the subscription marked as current and active
                    const currentActiveSubPkg = subscribedPackages.find(subPkg => {
                      const sub = subPkg?.subscription;
                      return sub && sub.current === true && sub.status && sub.status.toLowerCase() === 'active';
                    });
                    if (currentActiveSubPkg) {
                      const pkg = currentActiveSubPkg.package;
                      const sub = currentActiveSubPkg.subscription;
                      const planType = planTypes[sub?.id || sub?.Id];
                      const packageName = pkg?.name || pkg?.Name || 'Loading package...';
                      const displayName = planType ? `${packageName} (${planType})` : packageName;
                      
                      return <>
                        Current Plan: <span className="plan-name">{displayName}</span>
                      </>;
                    } else if (subscribedPackages.length > 0) {
                      return <span className="plan-name">All Your Subscriptions</span>;
                    } else {
                      return <span className="plan-name">Loading subscriptions...</span>;
                    }
                  })()}
                </h2>
                {/* Show status only if there is a current active subscription */}
                {(() => {
                  const currentActiveSubPkg = subscribedPackages.find(subPkg => {
                    const sub = subPkg?.subscription;
                    return sub && sub.current === true && sub.status && sub.status.toLowerCase() === 'active';
                  });
                  if (currentActiveSubPkg) {
                    return (
                      <p className="current-plan-status">
                        Status: <span className="status-current-active">Active</span>
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>
              
              {/* Show all subscriptions - always show if user has any subscriptions */}
              {subscribedPackages.length > 0 && (
                <div className="all-subscriptions-section">
                  <h3 className="all-subscriptions-title">
                    {subscribedPackages.length > 1 ? 'All Your Subscriptions:' : 'Your Subscription:'}
                  </h3>
                  <div className="subscriptions-list">
                    {subscribedPackages.map((subPackage, index) => {
                      const sub = subPackage?.subscription;
                      const pkg = subPackage?.package;
                      
                      // Safety check - skip if subscription is null/undefined
                      if (!sub) {
                        console.warn('Subscription is null/undefined at index', index);
                        return null;
                      }
                      
                      const isCurrent = sub.current === true;
                      const isActive = sub.status && sub.status.toLowerCase() === 'active';
                      const isCanceled = sub.status && sub.status.toLowerCase() === 'canceled';
                      
                      // Determine the display status and styling
                      let displayStatus, statusClass, actionButton = null;
                      
                      if (isCurrent && isActive) {
                        displayStatus = 'Current Active Plan';
                        statusClass = 'current-active';
                      } else if (!isCurrent && isActive) {
                        displayStatus = 'Subscribed';
                        statusClass = 'subscribed';
                        actionButton = (
                          <button 
                            className="activate-plan-button"
                            onClick={async () => {
                              try {
                                // console.log('Activating plan:', sub.id || sub.Id);
                                setError(null); // Clear any existing errors
                                setSuccessMessage(null); // Clear any existing success messages
                                
                                const response = await apiRequest('/api/usersubscription/current', {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json'
                                  },
                                  body: JSON.stringify(sub.id || sub.Id)
                                });
                                
                                // console.log('Plan activated successfully:', response);
                                // Refresh subscription data to update the UI
                                await refreshSubscription();
                                await fetchAllUserSubscriptionsAndPackages();
                                
                                // Show success message briefly
                                setSuccessMessage('Plan activated successfully!');
                                setTimeout(() => setSuccessMessage(null), 3000); // Clear after 3 seconds
                                
                              } catch (error) {
                                console.error('Error activating plan:', error);
                                setError('Failed to activate plan. Please try again.');
                              }
                            }}
                          >
                            Activate this plan
                          </button>
                        );
                      } else if (!isCurrent && isCanceled) {
                        displayStatus = 'Canceled Plan';
                        statusClass = 'canceled';
                      } else {
                        // Fallback for other states
                        displayStatus = sub.status || 'Active';
                        statusClass = (sub.status || '').toLowerCase();
                      }
                      
                      return (
                        <div key={sub.id || sub.Id || index} className={`subscription-item ${statusClass}`}>
                          <div className="subscription-package-name">
                            {(() => {
                              const packageName = pkg?.name || pkg?.Name || 'Unknown Package';
                              const planType = planTypes[sub?.id || sub?.Id];
                              return planType ? `${packageName} (${planType})` : packageName;
                            })()}
                          </div>
                          <div className="subscription-details">
                            <span className={`subscription-status status-${statusClass}`}>
                              {displayStatus}
                            </span>
                            {actionButton}
                          </div>
                        </div>
                      );
                    }).filter(Boolean)} {/* Filter out null entries */}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Simplified Step indicator - only show for payment step */}
        {currentStep === 3 && (
          <div className="step-indicator simplified">
            <div className="step completed">
              <span className="step-number">✓</span>
              <span className="step-label">Plan Selected</span>
            </div>
            <div className="step-connector"></div>
            <div className="step active">
              <span className="step-number">2</span>
              <span className="step-label">Complete Payment</span>
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">{error}</div>
        )}

        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}

        {/* Divider between subscriptions and package/plan selection (if user has subscriptions) */}
        {subscribedPackages.length > 0 && (
          <div className="subscriptions-divider" />
        )}

        {/* Combined Step 1 & 2: Package Selection + Plan Selection in one view */}
        {(currentStep === 1 || currentStep === 2) && (
          <div className="pricing-combined-section">
            {/* Package Selector - Compact tabs at top */}
            {packages.length > 1 && (
              <div className="package-selector-tabs">
                <h3 className="all-subscriptions-title">Subscribe to a new plan:</h3>
                <div className="package-tabs">
                  {packages.map(pkg => {
                    const isSelected = selectedPackage && (selectedPackage.id || selectedPackage.Id) === (pkg.id || pkg.Id);
                    const packageSubscription = subscribedPackages.find(subPkg =>
                      (pkg.id || pkg.Id) === (subPkg.package?.id || subPkg.package?.Id)
                    );
                    const isSubscribed = packageSubscription?.subscription?.status?.toLowerCase() === 'active';
                    
                    return (
                      <button
                        key={pkg.id || pkg.Id}
                        className={`package-tab ${isSelected ? 'active' : ''} ${isSubscribed ? 'subscribed' : ''}`}
                        onClick={() => handleMobilePackageSelection(pkg)}
                      >
                        {pkg.name || pkg.Name}
                        {isSubscribed && <span className="subscribed-badge">✓</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Billing Period Toggle */}
            <div className="billing-toggle-container">
              <div className="billing-toggle">
                <button
                  className={`${billingPeriod === 'monthly' ? 'active' : ''} billing-toggle-btn`}
                  onClick={() => handleBillingPeriodChange('monthly')}
                >
                  Monthly
                </button>
                <button
                  className={`${billingPeriod === 'annual' ? 'active' : ''} billing-toggle-btn`}
                  onClick={() => handleBillingPeriodChange('annual')}
                >
                  Annual <span className="save-badge">Save 25%</span>
                </button>
              </div>
            </div>

            {/* Plans Grid - Show immediately */}
            {plansLoading ? (
              <div className="loading-message">
                <div className="loading-spinner"></div>
                Loading plans...
              </div>
            ) : filteredPlans.length > 0 ? (
              <div className="plans-grid optimized">
                {[...filteredPlans]
                  .sort((a, b) => {
                    // Sort: Free first, then by price ascending
                    const aName = (a.name || a.Name || '').toLowerCase();
                    const bName = (b.name || b.Name || '').toLowerCase();
                    if (aName === 'free') return -1;
                    if (bName === 'free') return 1;
                    
                    const aRate = a.rates.find(r =>
                      billingPeriod === 'monthly'
                        ? r.monthlyPrice !== null && r.monthlyPrice !== undefined
                        : r.annualPrice !== null && r.annualPrice !== undefined
                    );
                    const bRate = b.rates.find(r =>
                      billingPeriod === 'monthly'
                        ? r.monthlyPrice !== null && r.monthlyPrice !== undefined
                        : r.annualPrice !== null && r.annualPrice !== undefined
                    );
                    const aPrice = billingPeriod === 'monthly' ? aRate?.monthlyPrice ?? Infinity : aRate?.annualPrice ?? Infinity;
                    const bPrice = billingPeriod === 'monthly' ? bRate?.monthlyPrice ?? Infinity : bRate?.annualPrice ?? Infinity;
                    return aPrice - bPrice;
                  })
                  .map(plan => {
                    const rate = plan.rates.find(r => {
                      if (billingPeriod === 'monthly') {
                        return r.monthlyPrice !== null && r.monthlyPrice !== undefined;
                      } else {
                        return r.annualPrice !== null && r.annualPrice !== undefined;
                      }
                    });

                    const price = billingPeriod === 'monthly' 
                      ? rate?.monthlyPrice 
                      : rate?.annualPrice;

                    const planName = plan.name || plan.Name || 'Plan';
                    const isFree = price === 0 || planName.toLowerCase() === 'free';
                    const isPremium = planName.toLowerCase().includes('premium');
                    const isStandard = planName.toLowerCase().includes('standard');
                    
                    // Check if this is the current subscribed plan
                    const isCurrentPlan = currentSubscriptionPlan && 
                      (plan.id || plan.Id) === (currentSubscriptionPlan.id || currentSubscriptionPlan.Id);
                    
                    // Determine button state and text
                    let canSelect = true;
                    let buttonText = isFree ? 'Start Free' : 'Select Plan';
                    let buttonClass = isFree ? 'start-free-button' : 'select-plan-button';
                    
                    if (currentSubscriptionRate && rate) {
                      const currentPrice = billingPeriod === 'monthly' 
                        ? currentSubscriptionRate.monthlyPrice 
                        : currentSubscriptionRate.annualPrice;
                      
                      if (isCurrentPlan) {
                        buttonText = 'Current Plan';
                        buttonClass = 'current-plan-btn';
                        canSelect = false;
                      } else if (price < currentPrice) {
                        buttonText = 'Downgrade Not Available';
                        buttonClass = 'not-available-btn';
                        canSelect = false;
                      } else if (price > currentPrice) {
                        buttonText = 'Upgrade Now';
                        buttonClass = 'upgrade-plan-btn';
                      }
                    } else if (!rate || price === null || price === undefined) {
                      buttonText = 'Not Available';
                      buttonClass = 'not-available-btn';
                      canSelect = false;
                    }

                    return (
                      <div 
                        key={plan.id || plan.Id}
                        className={`plan-card ${isFree ? 'free-plan-card' : ''} ${isPremium ? 'featured premium-plan' : ''} ${isStandard ? 'recommended' : ''} ${isCurrentPlan ? 'current' : ''}`}
                      >
                        {/* Plan badges */}
                        {isFree && <span className="plan-badge free-badge">GET STARTED</span>}
                        {isPremium && <span className="plan-badge premium-badge">BEST VALUE</span>}
                        {isStandard && !isPremium && <span className="plan-badge popular-badge">MOST POPULAR</span>}
                        {isCurrentPlan && <span className="plan-badge current-badge">YOUR PLAN</span>}
                        
                        <div className="plan-header">
                          <h3>{planName}</h3>
                          <div className="plan-price">
                            {isFree ? (
                              <>
                                <span className="price-amount free">Free</span>
                                <span className="price-period">forever</span>
                              </>
                            ) : (
                              <>
                                <span className="price-amount">
                                  ${price !== null && price !== undefined ? price.toFixed(2) : 'N/A'}
                                </span>
                                <span className="price-period">
                                  /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                                </span>
                              </>
                            )}
                          </div>
                          {isFree && (
                            <p className="no-card-required">No credit card required</p>
                          )}
                        </div>
                        
                        <div className="plan-description">
                          {plan.description || plan.Description || 'Comprehensive exam preparation'}
                        </div>

                        {/* Plan features */}
                        <div className="plan-features">
                          {plan.info && plan.info.length > 0 ? (
                            plan.info.map((feature, index) => {
                              const isIncluded = feature.isIncluded !== false && feature.IsIncluded !== false;
                              return (
                                <div key={index} className={`feature ${isIncluded ? 'included' : 'excluded'}`}>
                                  <span className="feature-check">
                                    {isIncluded ? '✓' : '✗'}
                                  </span>
                                  <div className="feature-content">
                                    <div className="feature-label">
                                      {feature.feature || feature.Feature || 'Feature'}
                                    </div>
                                    {(feature.detail || feature.Detail) && (
                                      <div className="feature-detail">
                                        {feature.detail || feature.Detail}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            // Fallback features
                            <div className="feature included">
                              <span className="feature-check">✓</span>
                              <div className="feature-content">
                                <div className="feature-label">Practice Questions</div>
                                <div className="feature-detail">
                                  {isFree ? 'Up to 5 per day' : 'Unlimited'}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <button
                          className={buttonClass}
                          onClick={() => canSelect ? handleMobilePlanSelection(plan) : null}
                          disabled={!canSelect}
                        >
                          {buttonText}
                          {isFree && canSelect && (
                            <svg className="arrow-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M5 12h14M12 5l7 7-7 7"/>
                            </svg>
                          )}
                        </button>
                      </div>
                    );
                  })}
              </div>
            ) : !selectedPackage ? (
              <div className="select-package-prompt">
                <div className="prompt-icon">📦</div>
                <h3>Select a Package Above</h3>
                <p>Choose your exam package to see available plans and pricing.</p>
              </div>
            ) : (
              <div className="no-plans-message">
                <h3>No plans available</h3>
                <p>No plans are available for this package at the moment.</p>
              </div>
            )}

            {packages.length === 0 && (
              <div className="no-packages-message">
                <p>No packages available at the moment. Please try again later.</p>
              </div>
            )}
          </div>
        )}

        {/* Legacy Step 1: Package Selection - Hidden, keeping for compatibility */}
        {false && currentStep === 1 && (
          <div className="packages-section">
            <h2>Select a Package</h2>
            <div className="packages-grid">
              {packages.map(pkg => {
                // Find the subscription for this package
                const packageSubscription = subscribedPackages.find(subPkg =>
                  (pkg.id || pkg.Id) === (subPkg.package?.id || subPkg.package?.Id)
                );
                const sub = packageSubscription?.subscription;

                // Determine status
                const isCurrent = sub?.current === true && sub?.status?.toLowerCase() === 'active';
                const isActive = sub?.status?.toLowerCase() === 'active';
                const isCanceled = sub?.status?.toLowerCase() === 'canceled';

                let packageStatus, packageStatusClass, packageTagText;
                if (isCurrent) {
                  packageStatus = 'Active';
                  packageStatusClass = 'current-active';
                  packageTagText = 'Active';
                } else if (isActive) {
                  packageStatus = 'Subscribed';
                  packageStatusClass = 'subscribed';
                  packageTagText = 'Subscribed';
                } else if (isCanceled) {
                  packageStatus = 'Canceled';
                  packageStatusClass = 'canceled';
                  packageTagText = 'Canceled';
                }

                return (
                  <div
                    key={pkg.id || pkg.Id}
                    className={`package-card${isCurrent ? ' active-package' : ''}${isActive && !isCurrent ? ' subscribed-package' : ''}`}
                    onClick={() => {
                      // Allow selection of all packages including current and subscribed ones
                      handleMobilePackageSelection(pkg);
                    }}
                    style={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      minHeight: '320px', // Ensures all cards have the same height
                      paddingBottom: '24px' // Extra space for button alignment
                    }}
                  >
                    {/* Active tag for current package */}
                    {isCurrent && (
                      <span 
                        className="active-package-tag"
                        style={{
                          position: 'absolute',
                          top: '12px',
                          right: '16px',
                          background: 'linear-gradient(90deg, #0b4724ff, #297546ff)',
                          color: '#fff',
                          padding: '2px 12px',
                          borderRadius: '12px',
                          fontWeight: 500,
                          fontSize: '0.85em',
                          letterSpacing: '0.2px',
                          zIndex: 2,
                          minWidth: 'auto',
                          width: 'fit-content',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                        }}
                      >
                        Active
                      </span>
                    )}
                    {/* Subscribed tag for subscribed (but not current) package */}
                    {!isCurrent && isActive && (
                      <span
                        className="subscribed-package-tag"
                        style={{
                          position: 'absolute',
                          top: '12px',
                          right: '16px',
                          background: 'linear-gradient(90deg, #154576ff, #3478afff)',
                          color: '#fff',
                          padding: '2px 12px',
                          borderRadius: '12px',
                          fontWeight: 500,
                          fontSize: '0.85em',
                          letterSpacing: '0.2px',
                          zIndex: 2,
                          minWidth: 'auto',
                          width: 'fit-content',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                        }}
                      >
                        Subscribed
                      </span>
                    )}
                    <h3>{pkg.name || pkg.Name || 'Package'}</h3>
                    <p className="package-description" style={{flex: '1 1 auto'}}>
                      {pkg.description || pkg.Description || 'No description available'}
                    </p>
                    <button
                      className={
                        isCurrent || (isActive && !isCurrent)
                          ? 'change-plan-button'
                          : 'select-package-button'
                      }
                      style={{
                        marginTop: 'auto',
                        marginBottom: '0',
                        alignSelf: 'center',
                        minWidth: '140px',
                        padding: '10px 24px',
                        fontSize: '1em',
                        fontWeight: (isCurrent || (isActive && !isCurrent)) ? 600 : 500,
                        letterSpacing: '0.2px',
                        transition: 'background 0.2s, color 0.2s'
                      }}
                    >
                      {isCurrent || (isActive && !isCurrent)
                        ? 'Change Plan'
                        : isActive && isCanceled
                          ? 'Resubscribe'
                          : 'Select Package'}
                    </button>
                  </div>
                );
              })}
            </div>
            
            {packages.length === 0 && (
              <div className="no-packages-message">
                No packages available at the moment. Please try again later.
              </div>
            )}
          </div>
        )}

        {/* Legacy Step 2: Plan Selection - Hidden, integrated into combined view above */}
        {false && currentStep === 2 && (
          <div className="plans-section">
            <div className="section-header">
              <button className="back-button" onClick={handleMobileBackToPackages}>
                ← Back to Packages
              </button>
              <h2>
                {currentSubscriptionPlan ? 
                  `Change Plan for ${selectedPackage?.name || selectedPackage?.Name}` :
                  `Select a Plan for ${selectedPackage?.name || selectedPackage?.Name}`
                }
              </h2>
            </div>

            {/* Billing Period Toggle */}
            <div className="billing-toggle-container">
              <div className="billing-toggle">
                <button
                  className={`${billingPeriod === 'monthly' ? 'active' : ''} billing-toggle-btn`}
                  onClick={() => handleBillingPeriodChange('monthly')}
                >
                  Monthly
                </button>
                <button
                  className={`${billingPeriod === 'annual' ? 'active' : ''} billing-toggle-btn`}
                  onClick={() => handleBillingPeriodChange('annual')}
                >
                  Annual (Save up to 25%)
                </button>
              </div>
            </div>

            {plansLoading ? (
              <div className="loading-message">Loading plans...</div>
            ) : (
              <div className="plans-grid">
                {[...filteredPlans]
                  .sort((a, b) => {
                    // Find the rate for each plan based on billing period
                    const aRate = a.rates.find(r =>
                      billingPeriod === 'monthly'
                        ? r.monthlyPrice !== null && r.monthlyPrice !== undefined
                        : r.annualPrice !== null && r.annualPrice !== undefined
                    );
                    const bRate = b.rates.find(r =>
                      billingPeriod === 'monthly'
                        ? r.monthlyPrice !== null && r.monthlyPrice !== undefined
                        : r.annualPrice !== null && r.annualPrice !== undefined
                    );
                    const aPrice = billingPeriod === 'monthly' ? aRate?.monthlyPrice ?? Infinity : aRate?.annualPrice ?? Infinity;
                    const bPrice = billingPeriod === 'monthly' ? bRate?.monthlyPrice ?? Infinity : bRate?.annualPrice ?? Infinity;
                    return aPrice - bPrice;
                  })
                  .map(plan => {
                    const rate = plan.rates.find(r => {
                      if (billingPeriod === 'monthly') {
                        return r.monthlyPrice !== null && r.monthlyPrice !== undefined;
                      } else {
                        return r.annualPrice !== null && r.annualPrice !== undefined;
                      }
                    });

                    const price = billingPeriod === 'monthly' 
                      ? rate?.monthlyPrice 
                      : rate?.annualPrice;

                    const planName = plan.name || plan.Name || 'Plan';
                    const isPopular = planName.toLowerCase().includes('premium');
                    
                    // Check if this is the current subscribed plan
                    const isCurrentPlan = currentSubscriptionPlan && 
                      (plan.id || plan.Id) === (currentSubscriptionPlan.id || currentSubscriptionPlan.Id);
                    
                    // Calculate upgrade/downgrade logic
                    let canSelect = true;
                    let buttonText = 'Select Plan';
                    let buttonClass = 'select-plan-button';
                    
                    if (currentSubscriptionRate && rate) {
                      const currentPrice = billingPeriod === 'monthly' 
                        ? currentSubscriptionRate.monthlyPrice 
                        : currentSubscriptionRate.annualPrice;
                      
                      if (isCurrentPlan) {
                        buttonText = 'Current Plan';
                        buttonClass = 'current-plan-btn';
                        canSelect = false;
                      } else if (price < currentPrice) {
                        // This is a downgrade - disable it
                        buttonText = 'Not Available';
                        buttonClass = 'not-available-btn';
                        canSelect = false;
                      } else if (price > currentPrice) {
                        // This is an upgrade
                        buttonText = 'Upgrade';
                        buttonClass = 'upgrade-plan-btn';
                      } else {
                        // Same price tier
                        buttonText = 'Select Plan';
                      }
                    } else if (!rate || price === null || price === undefined) {
                      buttonText = 'Not Available';
                      buttonClass = 'not-available-btn';
                      canSelect = false;
                    } else if (planName.toLowerCase() === 'free') {
                      buttonText = 'Start Free';
                      buttonClass = 'select-plan-button free-plan';
                    }
                    
                    // Override if this plan is selected
                    if (selectedPlan && (selectedPlan.id || selectedPlan.Id) === (plan.id || plan.Id)) {
                      buttonText = 'Selected';
                    }

                    return (
                      <div 
                        key={plan.id || plan.Id}
                        className={`plan-card ${isPopular ? 'featured' : ''} ${
                          selectedPlan && (selectedPlan.id || selectedPlan.Id) === (plan.id || plan.Id) ? 'selected-plan' : ''
                        }`}
                      >
                        {isPopular && <span className="best-value-tag">BEST VALUE</span>}
                        {isCurrentPlan && <span className="best-value-tag" style={{left: '20px', background: 'linear-gradient(90deg, #0b4724ff, #46a76bff)'}}>CURRENT PLAN</span>}
                        
                        <div className="plan-header">
                          <h3>{planName}</h3>
                          <div className="plan-price">
                            <span className="price-amount">
                              ${price !== null && price !== undefined ? price.toFixed(2) : 'N/A'}
                            </span>
                            <span className="price-period">
                              /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="plan-description">
                          {plan.description || plan.Description || 'Comprehensive exam preparation'}
                        </div>

                        {/* Render plan features from info */}
                        <div className="plan-features">
                          {plan.info && plan.info.length > 0 ? (
                            plan.info.map((feature, index) => {
                              const isIncluded = feature.isIncluded !== false && feature.IsIncluded !== false;
                              return (
                                <div key={index} className={`feature ${isIncluded ? 'included' : 'excluded'}`}>
                                  <span className="feature-check">
                                    {isIncluded ? '✓' : '✗'}
                                  </span>
                                  <div className="feature-content">
                                    <div className="feature-label">
                                      {feature.feature || feature.Feature || 'Feature'}
                                    </div>
                                    {(feature.detail || feature.Detail) && (
                                      <div className="feature-detail">
                                        {feature.detail || feature.Detail}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            // Fallback features based on plan name if no info available
                            <>
                              <div className="feature included">
                                <span className="feature-check">✓</span>
                                <div className="feature-content">
                                  <div className="feature-label">Practice Test</div>
                                  <div className="feature-detail">
                                    {planName.toLowerCase() === 'free' ? 'up to 5 questions per day' : 'Unlimited'}
                                  </div>
                                </div>
                              </div>
                              
                              {planName.toLowerCase() !== 'free' && (
                                <>
                                  <div className="feature included">
                                    <span className="feature-check">✓</span>
                                    <div className="feature-content">
                                      <div className="feature-label">Question Bank</div>
                                      <div className="feature-detail">Unlimited</div>
                                    </div>
                                  </div>
                                  
                                  <div className="feature included">
                                    <span className="feature-check">✓</span>
                                    <div className="feature-content">
                                      <div className="feature-label">Simulate Exam</div>
                                      <div className="feature-detail">
                                        {planName.toLowerCase().includes('standard') ? '5 Exam per month' : 'Unlimited'}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="feature included">
                                    <span className="feature-check">✓</span>
                                    <div className="feature-content">
                                      <div className="feature-label">Performance Analytics</div>
                                      <div className="feature-detail">Unlimited</div>
                                    </div>
                                  </div>
                                </>
                              )}

                              {planName.toLowerCase().includes('premium') && (
                                <>
                                  <div className="feature included">
                                    <span className="feature-check">✓</span>
                                    <div className="feature-content">
                                      <div className="feature-label">Adaptive Strength & Weakness Analyzer</div>
                                    </div>
                                  </div>
                                  
                                  <div className="feature included">
                                    <span className="feature-check">✓</span>
                                    <div className="feature-content">
                                      <div className="feature-label">OSCE Simulation (coming soon)</div>
                                    </div>
                                  </div>
                                  
                                  <div className="feature included">
                                    <span className="feature-check">✓</span>
                                    <div className="feature-content">
                                      <div className="feature-label">AI Explain (coming soon)</div>
                                    </div>
                                  </div>
                                  
                                  <div className="feature included">
                                    <span className="feature-check">✓</span>
                                    <div className="feature-content">
                                      <div className="feature-label">AI Chat (coming soon)</div>
                                    </div>
                                  </div>
                                </>
                              )}

                              {(planName.toLowerCase() === 'free' || planName.toLowerCase().includes('basic')) && (
                                <>
                                  <div className="feature excluded">
                                    <span className="feature-check">✗</span>
                                    <div className="feature-content">
                                      <div className="feature-label">Performance Analytics</div>
                                    </div>
                                  </div>
                                  
                                  <div className="feature excluded">
                                    <span className="feature-check">✗</span>
                                    <div className="feature-content">
                                      <div className="feature-label">Adaptive Strength & Weakness Analyzer</div>
                                    </div>
                                  </div>
                                  
                                  <div className="feature excluded">
                                    <span className="feature-check">✗</span>
                                    <div className="feature-content">
                                      <div className="feature-label">OSCE Simulation</div>
                                    </div>
                                  </div>
                                  
                                  <div className="feature excluded">
                                    <span className="feature-check">✗</span>
                                    <div className="feature-content">
                                      <div className="feature-label">AI Explain</div>
                                    </div>
                                  </div>
                                  
                                  <div className="feature excluded">
                                    <span className="feature-check">✗</span>
                                    <div className="feature-content">
                                      <div className="feature-label">AI Chat</div>
                                    </div>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                        
                        <button
                          className={buttonClass}
                          onClick={() => canSelect ? handleMobilePlanSelection(plan) : null}
                          disabled={!canSelect}
                        >
                          {buttonText}
                        </button>
                      </div>
                    );
                  })}
              </div>
            )}

            {filteredPlans.length === 0 && !plansLoading && (
              <div className="no-plans-message">
                <h3>No plans available for this package</h3>
                <p>This package doesn't have any available plans at the moment. Please try selecting a different package or contact support.</p>
                <button className="retry-button" onClick={handleMobileBackToPackages}>
                  ← Choose Different Package
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Payment Confirmation - Streamlined */}
        {currentStep === 3 && selectedPlan && selectedRate && (
          <div className="payment-section optimized">
            <div className="section-header">
              <button className="back-button" onClick={() => setCurrentStep(1)}>
                ← Change Plan
              </button>
              <h2>Complete Your Subscription</h2>
            </div>

            <div className="payment-layout">
              <div className="payment-summary">
                <div className="summary-card">
                  <h3>Order Summary</h3>
                  <div className="summary-item package-name">
                    <span className="label">Package</span>
                    <span className="value">{selectedPackage?.name || selectedPackage?.Name}</span>
                  </div>
                  <div className="summary-item plan-name">
                    <span className="label">Plan</span>
                    <span className="value">{selectedPlan?.name || selectedPlan?.Name}</span>
                  </div>
                  <div className="summary-item billing-cycle">
                    <span className="label">Billing</span>
                    <span className="value">{billingPeriod === 'monthly' ? 'Monthly' : 'Annual'}</span>
                  </div>
                  
                  <div className="summary-divider"></div>
                  
                  {/* Show pricing breakdown */}
                  <div className="summary-item subtotal">
                    <span className="label">Subtotal</span>
                    <span className="value">${(billingPeriod === 'monthly' 
                      ? selectedRate.monthlyPrice 
                      : selectedRate.annualPrice
                    ).toFixed(2)}</span>
                  </div>
                  
                  {/* Show discount if applied */}
                  {appliedPromo && discountedPrice && discountedPrice.savings != null && (
                    <div className="summary-item discount">
                      <span className="label">Discount</span>
                      <span className="value">-${discountedPrice.savings.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="summary-item total">
                    <span className="label">Total</span>
                    <span className="value">${(appliedPromo && discountedPrice && discountedPrice.finalPrice != null
                      ? discountedPrice.finalPrice.toFixed(2)
                      : (billingPeriod === 'monthly' 
                        ? selectedRate.monthlyPrice 
                        : selectedRate.annualPrice
                    ).toFixed(2))}</span>
                  </div>

                  {/* Trust signals */}
                  <div className="payment-trust-signals">
                    <span>🔒 Secure payment</span>
                    <span>↩️ Cancel anytime</span>
                  </div>
                </div>
              </div>
            </div>

              {/* Promotion Code section with Apply button */}
              <div className="promotion-code-section">
                {showReferralSelection ? (
                  // Show referral code selection UI when both codes are available
                  <div style={{ 
                    textAlign: 'center',
                    padding: '20px',
                    backgroundColor: isDarkMode ? '#1e3a5f' : '#f8f9fa',
                    borderRadius: '8px',
                    border: `2px solid ${isDarkMode ? '#4a90e2' : '#007bff'}`,
                    maxWidth: '500px',
                    width: '100%'
                  }}>
                    <div style={{ 
                      color: isDarkMode ? '#ffffff' : '#2c3e50', 
                      fontSize: '18px', 
                      fontWeight: 'bold',
                      marginBottom: '12px'
                    }}>
                      Multiple Codes are Available
                    </div>
                    <div style={{ 
                      color: isDarkMode ? '#e1e9ff' : '#6c757d', 
                      fontSize: '14px',
                      marginBottom: '20px'
                    }}>
                      You have promotional codes from multiple sources. Please choose one to apply:
                    </div>
                    
                    {/* Backend referral code option */}
                    {backendReferralCode && (
                      <div style={{
                        marginBottom: '12px',
                        padding: '12px',
                        backgroundColor: isDarkMode ? '#2a4a6f' : '#ffffff',
                        borderRadius: '6px',
                        border: `2px solid ${selectedReferralSource === 'backend' ? '#4caf50' : (isDarkMode ? '#4a90e2' : '#dee2e6')}`,
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedReferralSource('backend')}>
                        <input
                          type="radio"
                          name="referralSource"
                          value="backend"
                          checked={selectedReferralSource === 'backend'}
                          onChange={() => setSelectedReferralSource('backend')}
                          style={{ marginRight: '8px' }}
                        />
                        <strong style={{ color: isDarkMode ? '#ffffff' : '#2c3e50' }}>Your Account Code:</strong>{' '}
                        <span style={{ 
                          color: isDarkMode ? '#90cdf4' : '#007bff',
                          fontWeight: 'bold' 
                        }}>{backendReferralCode}</span>
                      </div>
                    )}
                    
                    {/* localStorage referral code option */}
                    {localStorageReferralCode && (
                      <div style={{
                        marginBottom: '20px',
                        padding: '12px',
                        backgroundColor: isDarkMode ? '#2a4a6f' : '#ffffff',
                        borderRadius: '6px',
                        border: `2px solid ${selectedReferralSource === 'localStorage' ? '#4caf50' : (isDarkMode ? '#4a90e2' : '#dee2e6')}`,
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedReferralSource('localStorage')}>
                        <input
                          type="radio"
                          name="referralSource"
                          value="localStorage"
                          checked={selectedReferralSource === 'localStorage'}
                          onChange={() => setSelectedReferralSource('localStorage')}
                          style={{ marginRight: '8px' }}
                        />
                        <strong style={{ color: isDarkMode ? '#ffffff' : '#2c3e50' }}>Referral Link Code:</strong>{' '}
                        <span style={{ 
                          color: isDarkMode ? '#90cdf4' : '#007bff',
                          fontWeight: 'bold' 
                        }}>{localStorageReferralCode}</span>
                      </div>
                    )}
                    
                    <button
                      onClick={() => {
                        const source = selectedReferralSource;
                        const code = source === 'backend' ? backendReferralCode : localStorageReferralCode;
                        applyReferralCodeBySource(source, code);
                      }}
                      disabled={!selectedReferralSource || promoLoading}
                      style={{
                        padding: '10px 24px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: (!selectedReferralSource || promoLoading) ? '#ccc' : '#4caf50',
                        color: 'white',
                        cursor: (!selectedReferralSource || promoLoading) ? 'not-allowed' : 'pointer',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        width: '100%'
                      }}
                    >
                      {promoLoading ? 'Applying...' : 'Apply Selected Code'}
                    </button>
                    
                    {/* Display error message if code validation fails */}
                    {promoError && (
                      <div style={{ 
                        color: '#dc3545', 
                        fontSize: '14px', 
                        textAlign: 'center',
                        marginTop: '12px',
                        fontWeight: '500'
                      }}>
                        {promoError}
                      </div>
                    )}
                  </div>
                ) : appliedPromo && discountedPrice && discountedPrice.savings != null && discountedPrice.savings > 0 ? (
                  // Show applied promo code message
                  <div style={{ 
                    textAlign: 'center',
                    padding: '16px',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '8px',
                    border: '2px solid #4caf50',
                    maxWidth: '400px',
                    width: '100%'
                  }}>
                    <div style={{ 
                      color: '#2e7d32', 
                      fontSize: '16px', 
                      fontWeight: 'bold',
                      marginBottom: '4px'
                    }}>
                      ✓ Promo Code Applied
                    </div>
                    <div style={{ 
                      color: '#388e3c', 
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}>
                      Code: <strong>{promoCode}</strong>
                      {selectedReferralSource && (
                        <span style={{ fontSize: '12px', marginLeft: '8px' }}>
                          ({selectedReferralSource === 'backend' ? 'Account Code' : 'Referral Link'})
                        </span>
                      )}
                    </div>
                    {/* Show discount details */}
                    {(
                      <div style={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid #4caf50',
                        color: '#1b5e20',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        {isPercentageDiscount(appliedPromo)
                          ? `${appliedPromo.discountPercentage}% off` 
                          : `$${appliedPromo.discountAmount} off`}
                        {' - '}
                        Saving ${discountedPrice.savings.toFixed(2)}
                      </div>
                    )}
                    {/* Show loading state while validating */}
                    {promoLoading && (
                      <div style={{
                        marginTop: '8px',
                        color: '#388e3c',
                        fontSize: '13px',
                        fontStyle: 'italic'
                      }}>
                        Validating code...
                      </div>
                    )}
                  </div>
                ) : (
                  // Show promo code input when no referral code
                  <>
                    <div className="promotion-code-row" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: '12px', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <label htmlFor="promoCode" style={{ fontWeight: 'bold', marginBottom: '4px' }}>Promotion Code:</label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input 
                            id="promoCode" 
                            type="text" 
                            className="promo-code-field" 
                            placeholder="Enter Code (Optional)" 
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value)}
                            disabled={promoLoading}
                            style={{ 
                              width: '180px', 
                              padding: '8px', 
                              borderRadius: '4px', 
                              border: '1px solid #ccc',
                              opacity: promoLoading ? 0.7 : 1
                            }} 
                          />
                          <button 
                            className="apply-promo-button"
                            onClick={handleApplyPromoCode}
                            disabled={promoLoading || !promoCode.trim()}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '4px',
                              border: 'none',
                              backgroundColor: promoLoading || !promoCode.trim() ? '#ccc' : '#007bff',
                              color: 'white',
                              cursor: promoLoading || !promoCode.trim() ? 'not-allowed' : 'pointer',
                              fontWeight: 'bold',
                              minWidth: '80px'
                            }}
                          >
                            {promoLoading ? 'Applying...' : 'Apply'}
                          </button>
                          {appliedPromo && (
                            <button 
                              className="remove-promo-button"
                              onClick={handleRemovePromoCode}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '4px',
                                border: '1px solid #dc3545',
                                backgroundColor: 'transparent',
                                color: '#dc3545',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Promo code messages */}
                    {promoError && (
                      <div style={{ 
                        color: '#dc3545', 
                        fontSize: '14px', 
                        textAlign: 'center',
                        marginTop: '4px'
                      }}>
                        {promoError}
                      </div>
                    )}
                    
                    {appliedPromo && (
                      <div style={{ 
                        color: '#28a745', 
                        fontSize: '14px', 
                        textAlign: 'center',
                        marginTop: '4px',
                        fontWeight: 'bold'
                      }}>
                        ✓ {appliedPromo.name} - {isPercentageDiscount(appliedPromo)
                          ? `${appliedPromo.discountPercentage}% off` 
                          : `$${appliedPromo.discountAmount} off`}
                      </div>
                    )}
                  </>
                )}
              </div>

            <div className="payment-actions" style={{ textAlign: 'center' }}>
              <button 
                className="buy-now-button"
                onClick={handleBuyNow}
              >
                <span style={{display: 'inline-flex', alignItems: 'center', gap: '0.6em'}}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{verticalAlign: 'middle'}}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
                  </svg>
                  Subscribe Now
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Cancel Subscription Flow - Show for users with any subscriptions */}
        {showCancelFlow && hasAnyActiveSubscription && (
          <div className="cancel-subscription-section">
            <div className="section-header">
              <button className="back-button" onClick={handleBackFromCancel}>
                ← Back
              </button>
              <h2>Cancel Subscription</h2>
            </div>

            <div className="cancel-intro">
              <p>Select the subscription you want to cancel:</p>
            </div>

            <div className="cancel-subscriptions-grid">
              {subscribedPackages.map((subPackage, index) => {
                const sub = subPackage?.subscription;
                const pkg = subPackage?.package;
                
                // Safety check - skip if subscription is null/undefined
                if (!sub) {
                  console.warn('Subscription is null/undefined at index', index);
                  return null;
                }
                
                const isCurrent = sub.current === true;
                const isActive = sub.status && sub.status.toLowerCase() === 'active';
                const isCanceled = sub.status && sub.status.toLowerCase() === 'canceled';
                const canCancel = isActive && sub.status.toLowerCase() !== 'canceled';
                
                // Determine the display status and styling (same logic as subscription list)
                let displayStatus, statusClass;
                
                if (isCurrent && isActive) {
                  displayStatus = 'Current Active Plan';
                  statusClass = 'current-active';
                } else if (!isCurrent && isActive) {
                  displayStatus = 'Subscribed';
                  statusClass = 'subscribed';
                } else if (!isCurrent && isCanceled) {
                  displayStatus = 'Canceled Plan';
                  statusClass = 'canceled';
                } else {
                  // Fallback for other states
                  displayStatus = sub.status || 'Active';
                  statusClass = (sub.status || '').toLowerCase();
                }
                
                return (
                  <CancelSubscriptionCard
                    key={sub.id || sub.Id || index}
                    subscription={sub}
                    package={pkg}
                    displayStatus={displayStatus}
                    statusClass={statusClass}
                    canCancel={canCancel}
                    cancellingSubscriptionId={cancellingSubscriptionId}
                    onCancelSubscription={handleCancelSubscription}
                  />
                );
              }).filter(Boolean)} {/* Filter out null entries */}
            </div>
          </div>
        )}

        {/* Cancel Subscription Button - Show at bottom for users with any subscriptions */}
        {hasAnyActiveSubscription && !showCancelFlow && currentStep === 1 && (
          <div className="cancel-subscription-footer">
            <button 
              className="show-cancel-button"
              onClick={handleShowCancelFlow}
            >
              Cancel Subscription
            </button>
          </div>
        )}

        {/* Cancel Confirmation Modal */}
        {showCancelConfirmation && (
          <div className="modal-overlay">
            <div className="cancel-confirmation-modal">
              <h3>Confirm Cancellation</h3>
              <p>Are you sure you want to cancel this subscription?</p>
              <p className="warning-text">This action cannot be undone. You will lose access to all features when your current billing period ends.</p>
              
              <div className="modal-actions">
                <button 
                  className="cancel-modal-button"
                  onClick={() => {
                    setShowCancelConfirmation(false);
                    setCancellingSubscriptionId(null); // Reset the processing state
                  }}
                >
                  Keep Subscription
                </button>
                <button 
                  className="confirm-cancel-button"
                  onClick={handleConfirmCancel}
                >
                  Yes, Cancel Subscription
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

// Helper function to get plan type for a subscription
const getPlanType = async (subscription) => {
  try {
    if (subscription?.planRateId) {
      const rateDetails = await apiRequest(`/api/plan/rate/${subscription.planRateId}`);
      const planId = rateDetails.planId || rateDetails.PlanId;
      
      if (planId) {
        const planDetails = await apiRequest(`/api/plan/${planId}`);
        const planName = planDetails.name || planDetails.Name || 'Unknown';
        
        // Check if it's a free plan
        const monthlyPrice = rateDetails.monthlyPrice || 0;
        const annualPrice = rateDetails.annualPrice || 0;
        const isFree = monthlyPrice === 0 && annualPrice === 0;
        
        return isFree ? 'Free' : planName;
      }
    }
    return 'Unknown';
  } catch (error) {
    console.warn('Error fetching plan type:', error);
    return 'Unknown';
  }
};

export default Billing;